import {FilterSelections, SearchPropertyModel, SearchTerms} from '../../../../../types/Search';
import {AggregationType} from "../../../../../types/AggregationType";

import {conceptQuery} from "../partials/concept-query-partial-builder";
import {ftsSuggestQuery} from "../partials/suggest-query-partial-builder";
import {relationsByTextQuery} from "../partials/relations-query-partial-builder";
import {getRelationsInfo} from "../../../utils/relation-utils";
import {CONCEPT_VAR, FROM_VAR, SIZE_VAR, TERMS_VAR} from "../variables";

import {bool} from "../../templates/compound-queries";
import * as TermQueries from "../../templates/term-queries";
import {trimQuery} from "../../templates/utils";
import aggregations from '../partials/aggregations-partial-builder';

const ftsQueryPartial = (terms: Array<string>, relations: Array<string>): string => {
  const termQueries = terms.map((term, index) => {
    const termVariable = `${TERMS_VAR}${index}`;
    return ftsSuggestQuery(termVariable) + relationsByTextQuery(relations, termVariable);
  }).join(',');

  return bool.should(termQueries);
};

const filtersPartial = (selections: FilterSelections): string => {
  const templates: Array<string> = [];
  Object.keys(selections).forEach((key: string) => {
    const selection = selections[key];
    const field = selection.field;

    switch (selection.facetType) {
      case AggregationType.DATERANGE:
        templates.push(dateRangeFilterPartial(field));
        break;
      case AggregationType.KEYWORD:
        templates.push(keywordFilterPartial(field));
        break;
      case AggregationType.NUMERIC_RANGE:
        templates.push(numericRangeFilterPartial(field));
        break;
    }
  });
  return templates.join(',');
};

const keywordFilterPartial = (field: string) => {
  return TermQueries.term({
    property: field,
    value: `$filter_${field}`
  });
};

const numericRangeFilterPartial = (field: string) => {
  return TermQueries.range({
    property: field,
    gte: `$filter_${field}_min`,
    lte: `$filter_${field}_max`
  });
};

const dateRangeFilterPartial = (field: string) => {
  return TermQueries.range({
    property: field,
    gte: `$filter_${field}_start`,
    lte: `$filter_${field}_end`
  });
};

const queryTemplate = (concept: string | undefined,
                       ftsTerms: Array<string>,
                       selections: FilterSelections,
                       relations: Array<string>,
                       conceptRelations: Array<string>,
                       properties: Array<SearchPropertyModel>,
                       withAggregations: boolean): string => {
  const queryBody = bool.must(`
    ${concept ? conceptQuery(CONCEPT_VAR, conceptRelations) : ''}
    ${concept && ftsTerms.length ? ',' : ''}
    ${ftsTerms.length ? ftsQueryPartial(ftsTerms, relations) : ''}
    ${Object.keys(selections).length ? ',' : ''}
    ${Object.keys(selections).length ? filtersPartial(selections) : ''}
  `);
  return `
    query: ${trimQuery(queryBody)}
    size: ${SIZE_VAR}
    from: ${FROM_VAR}
    ${withAggregations ? aggregations(properties) : ''}
  `;
};

export class SearchQueryArgumentBuilder {
  private searchTerms: SearchTerms = {concept: undefined, freeText: []};
  private searchableObjects: Map<string, Array<SearchPropertyModel>> = new Map();
  private selections: FilterSelections = {};
  private includeAggregations = true;

  setSearchTerms(searchTerms: SearchTerms): SearchQueryArgumentBuilder {
    this.searchTerms = searchTerms;
    return this;
  }

  setSearchableObjects(searchableObjects: Map<string, Array<SearchPropertyModel>>): SearchQueryArgumentBuilder {
    this.searchableObjects = searchableObjects;
    return this;
  }

  setSelections(selections: FilterSelections) {
    this.selections = selections;
    return this;
  }

  setIncludeAggregations(include: boolean): SearchQueryArgumentBuilder {
    this.includeAggregations = include;
    return this;
  }

  build(): string {
    const {properties, relations, conceptRelations} = getRelationsInfo(this.searchableObjects);

    return queryTemplate(
      this.searchTerms.concept?.id,
      this.searchTerms.freeText,
      this.selections,
      relations,
      conceptRelations,
      properties,
      this.includeAggregations);
  }
}
